<template>
    <div
        class="bg-transparent rounded-lg pt-2 pie-chart"
        v-if="chartVisibility"
        :class="{ 'flex items-center justify-center': !chartData || !chartData.series }"
    >
        <template v-if="chartData && chartData.series">
            <highcharts
                ref="pie-activity"
                class="chart-content_wrapper"
                :options="chartData"
                :callback="onInitChartCallback"
                :updateArgs="[true, true]"
            />
        </template>
        <template v-else>
            <div>
                <icon-no-data-cry class="w-64" />
                <h3 class="text-main-xl text-center">{{ $t('general.noData') }}</h3>
            </div>
        </template>
    </div>
</template>

<script>
import {TrashIcon} from 'vue-feather-icons'
import {Tooltip} from 'element-ui'
import get from 'lodash/get'
import groupBy from 'lodash/groupBy'
import Highcharts from 'highcharts'
import {Chart} from 'highcharts-vue'
import { USER_LIST_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'

import bus from '@/event-bus/EventBus'

import statusTypes, {statusNameLogOut} from '@/enum/statusTypes'
import widgetDataSourceTypes from '@/enum/widgetDataSourceTypes'
import extensionMixin from '@/mixins/extensionMixin'
import actionMixin from '@/components/Charts/Configs/actionMixin'

import { colorsForPieChartCallsPerAccColorsLayout } from '@/enum/colors'
import requestMixin from "@/mixins/requestMixin";

// If in future we should have support for other data types for realtime pie chart (at the moment only extension data type supported) - this should be removed and done vie some logic
const IS_EXTENSION_REALTIME_WIDGET = true

export default {
    mixins: [extensionMixin, actionMixin, requestMixin],
    components: {
        TrashIcon,
        highcharts: Chart,
        [Tooltip.name]: Tooltip,
    },
    props: {
        data: {
            type: Object,
            default: () => ({}),
        },
        editable: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            chartVisibility: true,
            chartData: {},
            HOLD_STATUS: 'Hold',
            chartInstance: false,
            fetchDataInterval: null,
        }
    },
    computed: {
        getLabelFontSize() {
            return get(this.data, 'WidgetLayout.labelFontSize', 16)
        },
        getDataLabelsColor() {
            return get(this.data, 'WidgetLayout.dataLabelsColor', '#000000')
        },
        getAccountStatuses() {
            return this.$store.getters['entities/accountStatuses']
        },
        isRealtimeDataSource() {
            return this.data.DataSourceTypeID === widgetDataSourceTypes.REAL_TIME_SOCKET
        }
    },
    methods: {
        // Generic
        initWidget() {
            this.setChartOptions()

            if (this.isRealtimeDataSource) {
                this.initRealtimeDataSource()
            } else {
                this.setChartOptionsWithRefreshInterval()
            }
        },
        async setChartOptions() {
            const chartData = await this.getChartSeriesData()
            const displayLegend = get(this.data, 'WidgetLayout.displayLegend', false)

            this.chartData = {
                chart: {
                    plotBackgroundColor: null,
                    plotBorderWidth: null,
                    plotShadow: false,
                    type: 'pie',
                    backgroundColor: 'transparent'
                },
                credits: {
                    enabled: false
                },
                plotOptions: {
                    pie: {
                        allowPointSelect: true,
                        cursor: 'pointer',
                        dataLabels: {
                            enabled: true,
                            format: this.$rtl.isRTL ? '{point.percentage:.1f}% : <b>{point.name}</b>' : '<b>{point.name}</b>: {point.percentage:.1f} %',
                            style: {
                                color: 'black',
                                textOutline: 'none',
                            },
                        },
                        showInLegend: displayLegend
                    },
                },
                ...chartData
            }

            this.reDrawChart()
        },
        setChartOptionsWithRefreshInterval() {
            if (this.fetchDataInterval) {
                clearInterval(this.fetchDataInterval)
            }

            const defaultRefreshIntervalInSec = get(this.data, 'DefaultRefreshInterval', 0) / 1000
            const refreshInterval = get(this.data, 'WidgetLayout.DefaultRefreshInterval', defaultRefreshIntervalInSec) * 1000

            if (refreshInterval) {
                this.fetchDataInterval = setInterval(async () => {
                    await this.setChartOptions()
                }, refreshInterval)
            }
        },
        getChartSeriesData() {
            if (this.isRealtimeDataSource) {
                // Update condition in case we will have different data with realtime
                if (IS_EXTENSION_REALTIME_WIDGET) {
                    return this.getExtensionsChartSeriesData()
                }
            } else {
                return this.getRequestWidgetData()
            }
        },
        async updateChartSeries() {
            const { colors, series } = await this.getChartSeriesData()

            this.$set(this.chartData, 'colors', colors)
            this.$set(this.chartData, 'series', series)
        },
        reDrawChart() {
            this.chartVisibility = false
            this.$nextTick(() => {
                this.chartVisibility = true
            })
        },
        triggerResizeEvent() {
            bus.$on('widget-resized', (widgetID) => {
                if (this.data.WidgetID.toString() !== widgetID.toString()) {
                    return;
                }
                this.reDrawChart()
            });
        },
        onInitChartCallback(chart) {
            this.chartInstance = chart
        },
        tryPrintChart() {
            const divToPrint = this.$el.children[0]
            const newWin = window.open();
            newWin.document.write(divToPrint.innerHTML);
            newWin.document.close();
            newWin.focus();
            newWin.print();
        },
        tryDownloadChart(type) {
            this.$refs['pie-activity'].chart.exportChart({
                type: type,
            })
        },
        getMappedColors(colors) {
            return Highcharts.map(colors, function (color) {
                return {
                    radialGradient: {
                        cx: 0.5,
                        cy: 0.3,
                        r: 0.7,
                    },
                    stops: [
                        [0, color],
                        [1, Highcharts.color(color).brighten(-0.3).get('rgb')], // darken
                    ],
                };
            })
        },
        // Non-realtime
        async getRequestWidgetData() {
            const data = await this.loadWidgetData(this.data)
            const initialColors = []

            if (!data) {
                return
            }

            data.forEach((el) => {
                if (el.name === 'Incoming' || el.name === 'Outgoing') {
                    initialColors.push(colorsForPieChartCallsPerAccColorsLayout[el.name])
                } else {
                    initialColors.push(el.color)
                }
                delete el.color
                return el
            });

            const series = [{
                colorByPoint: true,
                data,
                dataLabels: {
                    style: {
                        fontSize: this.getLabelFontSize,
                        color: this.getDataLabelsColor,
                    },
                },
            }]

            const colors = this.getMappedColors(initialColors)

            return {
                series,
                colors
            }
        },
        // RealTime
        initRealtimeDataSource() {
            let propToWatch

            if (IS_EXTENSION_REALTIME_WIDGET) {
                propToWatch = 'extensionWithCalls'
            }

            if (!propToWatch) {
                console.error('Pie chart in realtime mode has no source to watch')

                return
            }

            this.$watch(
                propToWatch,
                () => {
                    this.$nextTick(this.updateChartSeries)
                },
                {
                    deep: true
                }
            )
        },
        // Extension
        async getExtensionsChartSeriesData() {
            let data = this.getExtensionsData()
            const initialColors = []

            if (this.data.WidgetLayout.hideLoggedOff) {
                data = data.filter(el => el.name !== statusNameLogOut)
            }

            data.forEach((el) => {
                initialColors.push(el.color)
                delete el.color
                return el
            });

            const series = [{
                name: this.$t('widget.agents'),
                colorByPoint: true,
                data: data,
                dataLabels: {
                    style: {
                        fontSize: this.getLabelFontSize,
                        color: this.getDataLabelsColor,
                    },
                },
            }]

            const colors = this.getMappedColors(initialColors)

            return {
                series,
                colors,
            }
        },
        getExtensionsData() {
            let data = []
            let statusData = []
            let extensions = this.extensionWithCalls
            const userListConfig = this.data.WidgetConfig.find(config => config.ParameterName && config.ParameterName.toLowerCase() === USER_LIST_PARAMETER_NAME)
            const userList = userListConfig.WidgetParameterValueJson['EntityPositive']

            if (extensions.length) {
                statusData = groupBy(extensions, 'representativeStatus')
            }

            if (userList.length) {
                for (const item in statusData) {
                    statusData[item] = statusData[item].filter(el => {
                        return userList.includes(el.userID)
                    })
                }
            }

            for (let status in statusData) {
                let statusType = statusTypes[status]
                const statusText = this.$store.getters['entities/getStatusTextById'](status)
                const dynamicColor = this.getAccountStatuses.find(el => Number(el.StatusID) === Number(status))

                const color = dynamicColor && dynamicColor.ColorCode ? dynamicColor.ColorCode : statusType.color
                let sliceObject = {
                    color: color,
                    name: this.$t(statusText),
                    y: statusData[status].length,
                }

                if (!data.length) {
                    sliceObject.sliced = true
                    sliceObject.selected = true
                }

                data.push(sliceObject);
            }

            return data
        },
    },
    mounted() {
        this.triggerResizeEvent()
    },
    watch: {
        data: {
            immediate: true,
            handler () {
                this.initWidget()
            },
        }
    },
    beforeDestroy() {
        if (this.fetchDataInterval) {
            clearInterval(this.fetchDataInterval)
        }
    },
}
</script>

<style lang="scss" scoped>
.chart-content_wrapper {
    max-height: 390px;
}

.pie-chart ::v-deep .highcharts-title {
    @apply hidden;
}
</style>
